import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { Document, RelatedDocument, RelatedDocumentCountsByDataset } from '../../../core/store/models/document.model';
import { QueryResult, DocumentQueryParams } from '../../../core/store/models/query.model';
import { ExpressionParseResult } from '../../../core/store/models/auto-increment.model';
import { HttpService } from '../../../core/services/http.service';
import { DocumentReader } from 'src/app/modules/core/store/models/document-reader';
import { Dataset, DatasetMappings, DatasetSubtype, DatasetType } from 'src/app/modules/core/store/models/dataset.model';
import { DocumentSchema, GetRelatedDocumentsRequest } from './data.model';

import { API_BASE_URL as apiBaseUrl } from '@app/core/constants/api-base-url';

@Injectable({
  providedIn: 'root'
})
export class DataApiService {
  constructor(private http: HttpService) {}

  public getDocuments(datasetUId: string, query: DocumentQueryParams): Observable<QueryResult<Document>> {
    const queryString = this.http.queryStringFromObject({ includeCount: true });

    const url = `${apiBaseUrl}/v1/data/datasets/${datasetUId}/query?${queryString}`;

    return this.http.post(url, query);
  }

  public getDocumentCount(datasetUId: string, params: DocumentQueryParams): Observable<number> {
    let url = `${apiBaseUrl}/v1/data/datasets/${datasetUId}/count`;

    return this.http.post<number>(url, params);
  }

  public exportDocumentsToXlsx(datasetUId: string, query: DocumentQueryParams): Observable<any> {
    const url = `${apiBaseUrl}/v1/data/datasets/${datasetUId}/query/xlsx`;

    return this.http.post(url, query, {
      observe: 'response',
      responseType: 'blob'
    });
  }

  public updateDocumentWorkflowSteps(datasetUId: string, oldStepUId: string, workflowStepUId: string): Observable<Document[]> {
    const url = `${apiBaseUrl}/v1/data/datasets/${datasetUId}/updateWorkflowStep/${workflowStepUId}/${oldStepUId}`;

    return this.http.put(url);
  }

  public createDocument(datasetUId: string, document: Document): Observable<Document[]> {
    const url = `${apiBaseUrl}/v1/data/datasets/${datasetUId}/documents`;

    const createRequest = {
      documents: [document]
    };

    return this.http.post<Document[]>(url, createRequest);
  }

  public updateDocument(datasetUId: string, document: Document): Observable<Document[]> {
    const url = `${apiBaseUrl}/v1/data/datasets/${datasetUId}/documents`;

    const updateRequest = {
      documents: [document]
    };

    return this.http.put<Document[]>(url, updateRequest);
  }

  public deleteDocument(datasetUId: string, document: Document): Observable<void> {
    const reader = new DocumentReader(document);

    const url = `${apiBaseUrl}/v1/data/datasets/${datasetUId}/documents/${reader.uId}`;

    return this.http.delete(url);
  }

  public clearDatasetData(datasetUId: string): Observable<void> {
    const url = `${apiBaseUrl}/v1/data/datasets/${datasetUId}/data`;

    return this.http.delete(url);
  }

  public validateExpression(
    datasetUId: string,
    expression: string,
    startNumber: number,
    autoNumber: number
  ): Observable<ExpressionParseResult> {
    const url = `${apiBaseUrl}/v1/data/datasets/${datasetUId}/verify_expression`;
    const req = {
      expression: expression,
      startNumber: startNumber,
      autoNumber: autoNumber
    };

    return this.http.post<ExpressionParseResult>(url, req);
  }

  public relateDocuments(
    datasetUId: string,
    documentUId: string,
    toDatasetUId: string,
    toDocumentUIds: string[],
    asChild: boolean,
    includeReverse: boolean
  ): Observable<number> {
    const url = `${apiBaseUrl}/v1/data/datasets/${datasetUId}/documents/${documentUId}/children/${toDatasetUId}/add?asChild=${asChild}&includeReverse=${includeReverse}`;

    const req = {
      documentUIds: toDocumentUIds
    };

    return this.http.post(url, req);
  }

  public unRelateDocuments(
    datasetUId: string,
    documentUId: string,
    toDatasetUId: string,
    toDocumentUIds: string[],
    asChild: boolean,
    includeReverse: boolean
  ): Observable<number> {
    const url = `${apiBaseUrl}/v1/data/datasets/${datasetUId}/documents/${documentUId}/children/${toDatasetUId}/remove?asChild=${asChild}&includeReverse=${includeReverse}`;

    const req = {
      documentUIds: toDocumentUIds
    };

    return this.http.post(url, req);
  }

  public getRelatedDocuments(
    datasetUId: string,
    documentUId: string,
    toDatasetUId: string,
    query: DocumentQueryParams,
    skipParentCheck: boolean = false
  ): Observable<QueryResult<Document>> {
    const queryString = this.http.queryStringFromObject({ skipParentCheck: skipParentCheck, includeCount: true });

    const url = `${apiBaseUrl}/v1/data/datasets/${datasetUId}/documents/${documentUId}/children/${toDatasetUId}/query?${queryString}`;

    return this.http.post(url, query);
  }

  public getRelatedDocumentCountsByDataset(
    fromDatasetUId: string,
    fromDocumentUId?: string,
    targetDatasetType?: DatasetType,
    targetDatasetSubtype?: DatasetSubtype
  ): Observable<RelatedDocumentCountsByDataset[]> {
    const url = `${apiBaseUrl}/v1/data/datasets/${fromDatasetUId}/related-counts`;

    const request = {
      fromDocumentUId: fromDocumentUId,
      targetDatasetType: targetDatasetType,
      targetDatasetSubtype: targetDatasetSubtype
    } as RelatedDocumentCountsByDataset;

    return this.http.post(url, request);
  }

  public getDocument(datasetUId: string, documentUId: string): Observable<Document> {
    const url = `${apiBaseUrl}/v1/data/datasets/${datasetUId}/documents/${documentUId}`;

    return this.http.get(url);
  }

  public createMappedDocument(
    datasetUId: string,
    sourceDocument: Document,
    targetDataset: Dataset,
    defaultMapping?: DatasetMappings
  ): Observable<Document> {
    const url = `${apiBaseUrl}/v1/data/datasets/${datasetUId}/documents/mapped/init`;

    const request = {
      sourceDocument: sourceDocument,
      targetDataset: targetDataset,
      defaultMapping: defaultMapping
    };

    return this.http.post(url, request);
  }

  public getDocumentTranslations(datasetUId: string, documentUId: string): Observable<Document[]> {
    const url = `${apiBaseUrl}/v1/data/datasets/${datasetUId}/documents/${documentUId}/query/translations`;

    return this.http.get(url);
  }

  public getDocumentLanguages(datasetUId: string, documentUId: string): Observable<string[]> {
    const url = `${apiBaseUrl}/v1/data/datasets/${datasetUId}/documents/${documentUId}/query/languages`;

    return this.http.get(url);
  }

  public setDocumentTranslations(datasetUId: string, documentUId: string, translations: Document[]): Observable<void> {
    const url = `${apiBaseUrl}/v1/data/datasets/${datasetUId}/documents/${documentUId}/set/translations`;

    const request = {
      translations
    };

    return this.http.post(url, request);
  }

  public getSystemLogDocuments(subtype: DatasetSubtype, query: DocumentQueryParams): Observable<QueryResult<Document>> {
    const queryString = this.http.queryStringFromObject({ includeCount: true });

    const url = `${apiBaseUrl}/v1/data/datasets/${subtype}/exception-registry?${queryString}`;

    return this.http.post(url, query);
  }

  public getOrgBusinessUnitSchema(): Observable<DocumentSchema> {
    const url = `${apiBaseUrl}/v1/data/orgbusinessunits/schema`;

    return this.http.get(url);
  }

  public getDocumentAtTime(
    datasetUId: string,
    documentUId: string,
    notificationTime: string,
    documentUpdateTime?: string,
    cultureCode?: string
  ): Observable<Document> {
    const request = {
      datasetUId,
      documentUId,
      notificationTime,
      documentUpdateTime,
      cultureCode
    };
    let url = `${apiBaseUrl}/v1/data/datasets/document-at-time`;

    return this.http.post(url, request);
  }
}
